import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useRazorpay from "../../components/useRazorpay/useRazorpay";
import { getAddresses, addAddress, createCheckout, cancelOrder } from "./api";
import { useCart } from "./context/CartContext";
import ShopNavBar from "./components/ShopNavBar";
import Spinner from "./components/Spinner";
import { analytics, EVENTS } from "../../analytics";

const emptyAddress = {
  name: "",
  mobileNo: "",
  addressLine1: "",
  addressLine2: "",
  landmark: "",
  pincode: "",
  townCity: "",
  state: "",
};

function Field({ label, ...props }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium otaLight">{label}</span>
      <input
        className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#3969E7]/30 focus:border-[#3969E7] transition-shadow"
        {...props}
      />
    </label>
  );
}

export default function Checkout() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { items, clearCart } = useCart();
  const isRazorpayLoaded = useRazorpay({});

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState(emptyAddress);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [savingAddress, setSavingAddress] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAddresses()
      .then((res) => {
        const list = res.data.addresses || [];
        setAddresses(list);
        const preferred = list.find((a) => a.isDefault) || list[0];
        if (preferred) setSelectedAddressId(preferred.id);
        else setShowNewAddressForm(true);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingAddresses(false));
  }, []);

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setError(null);
    setSavingAddress(true);
    try {
      const res = await addAddress(newAddress);
      const created = res.data.address;
      const refreshed = await getAddresses();
      setAddresses(refreshed.data.addresses || []);
      setSelectedAddressId(created.id || refreshed.data.addresses?.[0]?.id);
      setShowNewAddressForm(false);
      setNewAddress(emptyAddress);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingAddress(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddressId) {
      setError("Please select or add a delivery address.");
      return;
    }
    setError(null);
    setPlacingOrder(true);
    try {
      const res = await createCheckout(shopId, {
        items: items.map((i) => ({
          productId: i.productId,
          productVariantId: i.productVariantId,
          quantity: i.quantity,
        })),
        deliveryAddressId: selectedAddressId,
        paymentMethod: "ONLINE",
      });
      const { data } = res;

      if (!data.needsPayment) {
        clearCart();
        navigate(`/shop/${shopId}/order-confirmed/${data.orderId}`);
        return;
      }

      if (!isRazorpayLoaded) {
        setError("Payment gateway is still loading, please try again.");
        setPlacingOrder(false);
        cancelOrder(data.orderId).catch(() => {});
        return;
      }

      const options = {
        key: data.razorpay.key,
        amount: data.razorpay.amount,
        currency: data.razorpay.currency,
        name: "Ōta",
        description: "Shop purchase",
        order_id: data.razorpay.orderId,
        handler: () => {
          clearCart();
          navigate(`/shop/${shopId}/order-confirmed/${data.orderId}`);
        },
        theme: { color: "#3969E7" },
        modal: {
          // User closed the payment sheet without paying — release the
          // reserved stock/points instead of leaving the order dangling.
          ondismiss: () => {
            analytics.track(EVENTS.CHECKOUT_DROPOFF, { step: "payment_sheet" });
            setPlacingOrder(false);
            cancelOrder(data.orderId).catch(() => {});
          },
        },
      };
      analytics.track(EVENTS.ADD_PAYMENT_INFO, { payment_type: "razorpay", currency: data.razorpay.currency });
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      analytics.track(EVENTS.PAYMENT_FAILED, { reason_code: "checkout_error" });
      setError(err.message);
      setPlacingOrder(false);
    }
  };

  return (
    <div className="min-h-screen pb-28">
      <ShopNavBar title="Checkout" showProfile={false} />

      <div className="px-4 pt-4 flex flex-col gap-4">
        <div>
          <p className="text-sm font-semibold otaDark mb-2">
            Delivery Address
          </p>

          {loadingAddresses && <Spinner label="Loading addresses…" />}

          {!loadingAddresses && (
            <div className="flex flex-col gap-2">
              {addresses.map((addr) => (
                <label
                  key={addr.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedAddressId === addr.id
                      ? "border-[#3969E7] bg-[#3969E7]/[0.06]"
                      : "border-gray-200 active:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    className="mt-1 accent-[#3969E7]"
                    checked={selectedAddressId === addr.id}
                    onChange={() => setSelectedAddressId(addr.id)}
                  />
                  <span className="text-sm otaDark">
                    <span className="font-semibold">{addr.name}</span> ·{" "}
                    {addr.mobileNo}
                    <br />
                    {addr.addressLine1}
                    {addr.addressLine2 ? `, ${addr.addressLine2}` : ""},{" "}
                    {addr.townCity}, {addr.state} - {addr.pincode}
                  </span>
                </label>
              ))}
            </div>
          )}

          {!loadingAddresses && !showNewAddressForm ? (
            <button
              onClick={() => setShowNewAddressForm(true)}
              className="mt-3 text-sm font-semibold otaFont flex items-center gap-1"
            >
              <span className="text-base leading-none">+</span> Add a new
              address
            </button>
          ) : !loadingAddresses ? (
            <form
              onSubmit={handleSaveAddress}
              className="mt-3 flex flex-col gap-3 p-3.5 rounded-xl border border-gray-200 bg-gray-50/60"
            >
              <Field
                required
                label="Full name"
                value={newAddress.name}
                onChange={(e) =>
                  setNewAddress((a) => ({ ...a, name: e.target.value }))
                }
              />
              <Field
                required
                label="Mobile number"
                value={newAddress.mobileNo}
                onChange={(e) =>
                  setNewAddress((a) => ({ ...a, mobileNo: e.target.value }))
                }
              />
              <Field
                required
                label="Address line 1"
                value={newAddress.addressLine1}
                onChange={(e) =>
                  setNewAddress((a) => ({
                    ...a,
                    addressLine1: e.target.value,
                  }))
                }
              />
              <Field
                label="Address line 2 (optional)"
                value={newAddress.addressLine2}
                onChange={(e) =>
                  setNewAddress((a) => ({
                    ...a,
                    addressLine2: e.target.value,
                  }))
                }
              />
              <Field
                label="Landmark (optional)"
                value={newAddress.landmark}
                onChange={(e) =>
                  setNewAddress((a) => ({ ...a, landmark: e.target.value }))
                }
              />
              <div className="grid grid-cols-2 gap-3">
                <Field
                  required
                  label="City"
                  value={newAddress.townCity}
                  onChange={(e) =>
                    setNewAddress((a) => ({ ...a, townCity: e.target.value }))
                  }
                />
                <Field
                  required
                  label="State"
                  value={newAddress.state}
                  onChange={(e) =>
                    setNewAddress((a) => ({ ...a, state: e.target.value }))
                  }
                />
              </div>
              <Field
                required
                label="Pincode"
                value={newAddress.pincode}
                onChange={(e) =>
                  setNewAddress((a) => ({ ...a, pincode: e.target.value }))
                }
              />
              <div className="flex gap-2 mt-1">
                <button
                  type="submit"
                  disabled={savingAddress}
                  className="flex-1 otaGradientButton text-white text-sm font-semibold rounded-lg py-2.5 disabled:opacity-60 active:scale-[0.98] transition-transform"
                >
                  {savingAddress ? "Saving…" : "Save Address"}
                </button>
                {addresses.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowNewAddressForm(false)}
                    className="px-4 text-sm font-medium otaLight"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          ) : null}
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3 text-center">
            {error}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] p-4 bg-white border-t border-gray-100">
        <button
          onClick={handlePlaceOrder}
          disabled={placingOrder || !selectedAddressId}
          className="w-full otaGradientButton text-white font-semibold rounded-xl py-3.5 disabled:opacity-50 active:scale-[0.98] transition-transform disabled:active:scale-100 flex items-center justify-center gap-2"
        >
          {placingOrder && (
            <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
          )}
          {placingOrder ? "Placing order…" : "Place Order & Pay"}
        </button>
      </div>
    </div>
  );
}
